import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Heart } from "lucide-react";
import { METER_QUIZZES } from "@/lib/meterQuizzes";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Play() {
  usePageMeta(
    "Fun dog quizzes — Fur-Meter, Chaos Meter & more | Fetchright",
    "Playful 10-second dog quizzes: find the breed that matches your fur tolerance, energy and vibe. Share your result card. Free, no sign-up."
  );

  const quizzes = Object.values(METER_QUIZZES);

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#F2CC8F]/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-[#E07A5F]/20 blur-3xl" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
            <Sparkles size={13} /> Fetchright Fun
          </span>
          <h1 className="mt-6 font-serif font-black tracking-tight text-accent text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            10-second dog quizzes
          </h1>
          <p className="mt-5 text-card-foreground/70 text-base md:text-lg">
            Slide, reveal, and get a share-ready result card. Perfect for a screenshot — no sign-up, totally free.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-6" data-testid="play-grid">
          {/* Speed Dating — featured game */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/speed-dating"
              data-testid="play-card-speed-dating"
              className="group block rounded-[2rem] bg-card border border-border p-8 shadow-[0_8px_30px_rgba(224,122,95,0.12)] hover:-translate-y-1 transition-transform h-full"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-widest">
                <Heart size={12} /> Dog Speed Dating
              </span>
              <h2 className="mt-5 font-serif font-black text-accent text-2xl md:text-3xl leading-tight">
                Swipe your way to your type
              </h2>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                Like or pass on dogs, rapid-fire, and discover what your swipes say about you.
              </p>
              <div className="mt-6 flex items-center gap-2 font-bold text-primary">
                Play now
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </motion.div>

          {quizzes.map((q, i) => {
            const Icon = q.BadgeIcon;
            return (
              <motion.div
                key={q.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <Link
                  to={`/${q.slug}`}
                  data-testid={`play-card-${q.slug}`}
                  className="group block rounded-[2rem] bg-card border border-border p-8 shadow-[0_8px_30px_rgba(224,122,95,0.12)] hover:-translate-y-1 transition-transform h-full"
                >
                  <span className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-widest">
                    <Icon size={12} /> {q.brandBadge}
                  </span>
                  <h2 className="mt-5 font-serif font-black text-accent text-2xl md:text-3xl leading-tight">
                    {q.headline[0]} {q.headline[1]}
                  </h2>
                  <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{q.sub}</p>
                  <div className="mt-6 flex items-center gap-2 font-bold text-primary">
                    Play now
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            );
          })}

          {/* Full quiz card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: quizzes.length * 0.08 }}
            className="sm:col-span-2"
          >
            <Link
              to="/quiz"
              data-testid="play-card-full-quiz"
              className="group flex flex-col sm:flex-row sm:items-center gap-4 justify-between rounded-[2rem] bg-accent text-accent-foreground p-8 warm-shadow hover:-translate-y-1 transition-transform"
            >
              <div>
                <span className="text-[#F2CC8F] text-xs font-bold uppercase tracking-widest">Want the real answer?</span>
                <h2 className="mt-2 font-serif font-black text-2xl md:text-3xl leading-tight">
                  Take the full 15-question match quiz
                </h2>
                <p className="mt-2 text-accent-foreground/75 text-sm">
                  Lifestyle, home, budget and more — your genuine top breed match in 2 minutes.
                </p>
              </div>
              <span className="pill-btn inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-bold shrink-0">
                Start <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
